import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, User, Save, Loader2, Lock, Trash2, AlertTriangle } from 'lucide-react';

const GABARIT_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const NIVEAU_OPTIONS = ['Débutant', 'Intermédiaire', 'Confirmé', 'Expert'];

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [profile, setProfile] = useState({ name: '', phone: '', age: '', gabarit: '', niveau_pratique: '', adresse_postale: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    authAPI.me().then(r => {
      const d = r.data;
      setProfile({
        name: d.name || '', phone: d.phone || '',
        age: d.age || '', gabarit: d.gabarit || '',
        niveau_pratique: d.niveau_pratique || '', adresse_postale: d.adresse_postale || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      if (payload.age) payload.age = parseInt(payload.age, 10) || null;
      const res = await authAPI.updateProfile(payload);
      updateUser({ ...user, name: payload.name });
      toast.success('Profil mis à jour');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setChangingPwd(true);
    try {
      await authAPI.changePassword({ current_password: passwords.current_password, new_password: passwords.new_password });
      toast.success('Mot de passe modifié');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setChangingPwd(false);
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error('Mot de passe requis'); return; }
    setDeletingAccount(true);
    try {
      await authAPI.deleteAccount({ password: deletePassword });
      toast.success('Compte supprime avec succes');
      localStorage.removeItem('rw_token');
      localStorage.removeItem('rw_user');
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
    setDeletingAccount(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-[#00FF00] text-sm mb-6 transition-colors" data-testid="profile-back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="flex items-center gap-4 mb-10">
          <div className="w-16 h-16 rounded-full bg-[#00FF00] flex items-center justify-center">
            <span className="text-black text-2xl font-bold">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="profile-title">Mon profil</h1>
            <p className="text-slate-500 text-sm">{user?.email}</p>
          </div>
        </div>

        {/* Informations personnelles */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-white mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-[#00FF00]" /> Informations personnelles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-400">Nom complet</Label>
              <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Téléphone</Label>
              <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+33..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-phone" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Âge</Label>
              <Input type="number" value={profile.age} onChange={e => setProfile(p => ({ ...p, age: e.target.value }))} placeholder="25" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-age" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Gabarit</Label>
              <Select value={profile.gabarit || undefined} onValueChange={v => setProfile(p => ({ ...p, gabarit: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-gabarit">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {GABARIT_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Niveau de pratique</Label>
              <Select value={profile.niveau_pratique || undefined} onValueChange={v => setProfile(p => ({ ...p, niveau_pratique: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-niveau">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {NIVEAU_OPTIONS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-400">Adresse postale</Label>
              <Input value={profile.adresse_postale} onChange={e => setProfile(p => ({ ...p, adresse_postale: e.target.value }))} placeholder="123 Rue de la Mer, 83400 Hyères" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-address" />
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="mt-6 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="profile-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>

        {/* Mot de passe */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#00FF00]" /> Changer le mot de passe
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400">Mot de passe actuel</Label>
              <Input type="password" value={passwords.current_password} onChange={e => setPasswords(p => ({ ...p, current_password: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-current-pwd" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Nouveau mot de passe</Label>
              <Input type="password" value={passwords.new_password} onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-new-pwd" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Confirmer le nouveau mot de passe</Label>
              <Input type="password" value={passwords.confirm_password} onChange={e => setPasswords(p => ({ ...p, confirm_password: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="profile-confirm-pwd" />
            </div>
          </div>
          <Button onClick={handlePasswordChange} disabled={changingPwd || !passwords.current_password || !passwords.new_password} variant="outline" className="mt-6 border-[#00FF00]/30 text-[#00FF00] hover:bg-[#00FF00]/10 font-bold" data-testid="profile-change-pwd-btn">
            {changingPwd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            Modifier le mot de passe
          </Button>
        </div>

        {/* Supprimer mon compte */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-red-500/20 p-6 mt-6">
          <h2 className="font-heading text-lg font-bold text-red-400 mb-4 flex items-center gap-2">
            <Trash2 className="w-5 h-5" /> Supprimer mon compte
          </h2>
          {!showDeleteAccount ? (
            <div>
              <p className="text-sm text-slate-400 mb-4">
                Cette action est irreversible. Votre compte sera supprime et vos reservations seront anonymisees.
              </p>
              <Button
                onClick={() => setShowDeleteAccount(true)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Je souhaite supprimer mon compte
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="delete-account-confirm">
              <div className="flex items-start gap-3 bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-400">Attention</p>
                  <p className="text-xs text-slate-400 mt-1">Votre compte et vos donnees personnelles seront definitivement supprimes. Vos reservations seront conservees de maniere anonyme pour l'historique.</p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Confirmez votre mot de passe</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="bg-[#0A0A0A] border-red-500/30 text-white mt-1"
                  data-testid="delete-account-password"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || !deletePassword}
                  className="bg-red-500 text-white hover:bg-red-600 font-bold"
                  data-testid="confirm-delete-account-btn"
                >
                  {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Supprimer definitivement
                </Button>
                <Button
                  onClick={() => { setShowDeleteAccount(false); setDeletePassword(''); }}
                  variant="outline"
                  className="border-[#2A2A2A] text-slate-400 bg-transparent"
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
